import { pxToEm } from 'util/pxToEm';
import { convertTo12Hour } from 'util/convertTimeString';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  styled,
  Backdrop,
  useTheme,
} from '@mui/material';
import { Event, useEventsApi } from 'services/api';
import { Button } from 'components/ui';
import { useSnackbar } from 'notistack';
import { useProfile } from 'hooks';
import { useReceiptsApi } from 'services/api/hooks/useReceiptsApi.hook';
import { ReserveTicketsRequest } from 'services/api/apis/events/dto/reserve-tickets.dto';
import { pollReceiptUntilTerminal } from 'util/pollReceipt';
import { EventCheckInSheet } from './event-check-in';

interface TimeSlotModalProps {
  open: boolean;
  onClose: () => void;
  event: Event;
  onReservationComplete?: () => void;
}

const backdropComponent = styled(Backdrop, {
  name: 'MuiModal',
  slot: 'Backdrop',
  overridesResolver: (props, styles) => {
    return styles.backdrop;
  },
})({
  bottom: 0,
});

export const TimeSlotModal: React.FC<TimeSlotModalProps> = ({
  open,
  onClose,
  event,
  onReservationComplete,
}) => {
  const theme = useTheme();
  const { profile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();
  const eventsApi = useEventsApi();
  const receiptsApi = useReceiptsApi();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>();
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketModalData, setTicketModalData] = useState<{
    receiptId: string;
    tickets: any[];
  } | null>(null);

  useEffect(() => {
    if (!receiptId) return;

    const cancel = pollReceiptUntilTerminal(
      receiptsApi.getReceipt,
      receiptId,
      {
        onComplete: receipt => {
          setTicketModalData({
            receiptId,
            tickets: receipt.tickets || [],
          });
          setShowTicketModal(true);
          setReceiptId(null);
          onReservationComplete?.();
          onClose();
        },
        onFailed: message => {
          enqueueSnackbar(message, { variant: 'error' });
          setReceiptId(null);
        },
        onGaveUp: () => {
          // The reservation likely persisted; surface it and let caches refresh.
          enqueueSnackbar(
            'Your reservation is taking longer than expected. Please check your tickets in a moment.',
            { variant: 'warning' },
          );
          setReceiptId(null);
          onReservationComplete?.();
          onClose();
        },
      },
    );

    return cancel;
  }, [receiptId]);

  const handleReserve = async () => {
    if (isSubmitting || receiptId) return;

    if (!selectedTimeSlot) {
      enqueueSnackbar('Please select a time slot', {
        variant: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const ticketsRequest: ReserveTicketsRequest = {
        source:
          typeof window !== 'undefined' && window.ReactNativeWebView
            ? 'native'
            : 'web',
        tickets: [
          {
            firstName: profile?.firstName || '',
            lastName: profile?.lastName || '',
            email: profile?.primaryEmail || profile?.emails?.[0] || '',
            timeSlotId: selectedTimeSlot,
          },
        ],
      };

      const result = await eventsApi.reserveTickets(event.id, ticketsRequest);

      if (!result.success) {
        enqueueSnackbar(result.error.message, { variant: 'error' });
        return;
      }

      const { receiptId: newReceiptId } = result.value;
      setReceiptId(newReceiptId);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        BackdropComponent={backdropComponent}
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          onClose();
        }}
        sx={{
          '& .MuiBackdrop-root': {
            position: 'fixed',
          },
          '& .MuiDialog-paper': {
            position: { xs: 'absolute', md: 'relative' },
            bottom: { xs: 0, md: 'unset' },
            margin: { xs: 0, md: 32 },
            borderRadius: { xs: '20px 20px 0 0', md: 20 },
            width: { xs: '100%', md: 500 },
            maxWidth: { xs: '100%', md: 500 },
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
        maxWidth={false}
      >
        <Box
          sx={{
            borderRadius: { xs: '20px 20px 0 0', md: 20 },
            backgroundColor: theme.palette.background.paper,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              flexDirection: 'column',
              px: { xs: 20, md: 32 },
              pt: 32,
              pb: { xs: 24, md: 32 },
            }}
          >
            <Typography
              sx={{
                fontSize: pxToEm(24),
                fontWeight: 500,
                color: theme.palette.text.primary,
                mb: 16,
                lineHeight: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {event.name}
            </Typography>

            <Typography
              sx={{
                fontSize: pxToEm(14),
                color: theme.palette.text.primary,
                mb: 16,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              Registering as {profile?.firstName} {profile?.lastName}
            </Typography>

            <Typography
              sx={{
                fontSize: pxToEm(14),
                fontWeight: 500,
                color: theme.palette.text.primary,
                mb: 16,
              }}
            >
              Select Your Time Slot
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr' },
                width: '100%',
                gap: pxToEm(24),
                mb: 32,
              }}
            >
              {event.timeSlots?.map(item => (
                <Box
                  key={item.id}
                  sx={{
                    border: `1px solid ${theme.palette.primary.main}`,
                    px: { xs: 12, md: 16 },
                    py: 16,
                    background:
                      item.id === selectedTimeSlot
                        ? theme.palette.primary.main
                        : theme.palette.common.white,
                    color:
                      item.id === selectedTimeSlot
                        ? theme.palette.common.white
                        : theme.palette.primary.main,
                    cursor:
                      item.reservations >= item.quantity
                        ? 'not-allowed'
                        : 'pointer',
                    fontSize: { xs: 13, md: 14 },
                    opacity: item.reservations >= item.quantity ? 0.3 : 1,
                    textAlign: 'center',
                    borderRadius: 4,
                    transition: 'all 0.2s',
                    '&:hover': {
                      opacity:
                        item.reservations >= item.quantity
                          ? 0.3
                          : item.id === selectedTimeSlot
                          ? 1
                          : 0.8,
                    },
                  }}
                  onClick={() => {
                    if (item.reservations >= item.quantity) return;
                    setSelectedTimeSlot(item.id);
                  }}
                >
                  {convertTo12Hour(item.time, { includePeriod: true })} -{' '}
                  {convertTo12Hour(item.endTime, { includePeriod: true })}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                mb: 32,
              }}
            >
              <Button
                variant="vibrant"
                onClick={handleReserve}
                acting={!!receiptId}
                disabled={isSubmitting || !!receiptId}
                sx={{
                  width: 320,
                  height: 47,
                  minHeight: 47,
                  maxHeight: 47,
                  padding: 16,
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 8,
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 400,
                }}
              >
                {isSubmitting || receiptId ? 'Reserving...' : 'Reserve'}
              </Button>
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                mb: 48,
              }}
            >
              <Typography
                onClick={onClose}
                sx={{
                  fontSize: pxToEm(14),
                  color: theme.palette.text.primary,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                Cancel
              </Typography>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {ticketModalData && (
        <EventCheckInSheet
          open={showTicketModal}
          onClose={() => setShowTicketModal(false)}
          receiptId={ticketModalData.receiptId}
          tickets={ticketModalData.tickets.map(t => ({
            firstName: t.firstName,
            lastName: t.lastName,
            email: t.email,
            ticketNumber: t.ticketNumber,
            typeId: t.typeId,
            checkedInDate: t.checkInDate,
          }))}
          event={event}
        />
      )}
    </>
  );
};

export default TimeSlotModal;
